import type { InventoryItemOutcome } from "@redotech/crdb-enums/inventory/inventory-item-outcome";

/** One stocked inventory level in a bin being considered for a bin flush. */
interface BinFlushUnit {
  inventoryItemId: string | null;
  productAccountId: string | null;
  outcome: InventoryItemOutcome | null;
  quantity: number;
}

export enum BinFlushBlockReason {
  EMPTY = "empty",
  /** Bulk stock with no inventory item can't be checked for an outcome. */
  UNTRACKED = "untracked",
  OTHER_MERCHANT = "other_merchant",
  OUTCOME_MISMATCH = "outcome_mismatch",
}

export type BinFlushPlan =
  | { kind: "flush"; unitCount: number }
  | { kind: "blocked"; reason: BinFlushBlockReason; unitCount: number };

/**
 * A bin flushes whole only when every stocked unit is a tracked item of the
 * merchant tagged with the outcome; else the first failing rule and its count.
 */
export function planBinFlush(
  units: readonly BinFlushUnit[],
  params: {
    merchantAccountIds: readonly string[];
    outcome: InventoryItemOutcome;
  },
): BinFlushPlan {
  const merchants = new Set(params.merchantAccountIds);
  const stocked = units.filter((unit) => unit.quantity > 0);
  if (stocked.length === 0) {
    return { kind: "blocked", reason: BinFlushBlockReason.EMPTY, unitCount: 0 };
  }

  const checks: [BinFlushBlockReason, (unit: BinFlushUnit) => boolean][] = [
    [BinFlushBlockReason.UNTRACKED, (unit) => unit.inventoryItemId === null],
    [
      BinFlushBlockReason.OTHER_MERCHANT,
      (unit) =>
        unit.productAccountId === null || !merchants.has(unit.productAccountId),
    ],
    [
      BinFlushBlockReason.OUTCOME_MISMATCH,
      (unit) => unit.outcome !== params.outcome,
    ],
  ];
  for (const [reason, fails] of checks) {
    const failing = stocked.filter(fails);
    if (failing.length > 0) {
      return { kind: "blocked", reason, unitCount: sumQuantity(failing) };
    }
  }

  return { kind: "flush", unitCount: sumQuantity(stocked) };
}

function sumQuantity(units: readonly BinFlushUnit[]): number {
  return units.reduce((sum, unit) => sum + unit.quantity, 0);
}
